<template>
  <div
    :class="{ 'is-selfie-active': selfieActive, 'scrollable-container': isMobile }"
    class="page-selfie"
  >
    <loading-preparation
      v-if="isPreparingEnvironment"
      :title="messages.loadingEnvironment"
    />
    <loading-preparation
      v-else-if="isInitializingCapture"
      :title="messages.loadingCamera"
    />
    <loading-preparation
      v-else-if="isSubmittingSelfie"
      :show-spinner="true"
      :title="messages.processing"
    />
    <section
      v-else-if="!startCapture && !alert.show"
      class="section-centered section-sm"
    >
      <div class="container">
        <div class="row row-grid justify-content-center">
          <div class="text-center">
            <div>
              <img
                class="selfie-ilustration"
                src="img/icons/selfie/SelfieBiometricsIllustration.png"
                alt="selfieIlustration"
              >
            </div>
            <div>
              <h2 class="display-3">Vamos validar sua selfie</h2>
              <p class="heading-subtitle">Dicas para tirar uma boa imagem</p>
            </div>
            <div class="instructions">
              <div class="content-instruction">
                <img
                  src="img/icons/common/LightIcon.png"
                  alt="IconLight"
                >
                <p>Escolha um local iluminado</p>
              </div>
              <div class="content-instruction">
                <img
                  src="img/icons/common/NoGlasses.png"
                  alt="IconGlasses"
                >
                <p>Não use óculos, chapéu nem boné</p>
              </div>
              <div class="content-instruction">
                <img
                  src="img/icons/common/EmotionSatisfied.png"
                  alt="IconHair"
                >
                <p>Evite deixar o cabelo no rosto</p>
              </div>
              <div class="content-instruction">
                <img
                  src="img/icons/common/FocusFace.png"
                  alt="IconFace"
                >
                <p>Enquadre o seu rosto</p>
              </div>
              <div class="content-instruction">
                <img
                  src="img/icons/common/ClearBackground.png"
                  alt="IconBackground"
                >
                <p>Use um fundo claro e sem estampas</p>
              </div>
            </div>
            <button
              @click="capture()"
              type="submit"
              class="btn btn-primary mb-3 mb-sm-0"
            >
              Estou pronto
            </button>
          </div>
        </div>
      </div>
    </section>
    <div
      v-else
      class="position-relative"
    >
      <div class="container">
        <div class="row row-grid justify-content-center">
          <div
            v-if="!selfieActive && !alert.show"
            class="col-lg-12 text-center"
          >
            <div class="d-flex flex-column justify-space-between align-center">
              <div class="flex-item item flex-image">
                <img
                  v-lazy="'img/icons/selfie/selfie_icon.png'"
                  class="card-img"
                >
              </div>
            </div>
            <div>
              <h1 class="display-3 ">Vamos validar<br>sua selfie</h1>
              <p class="lead">
                Hora de tirar sua foto de identificação.
              </p>
              <div class="btn-wrapper">
                <base-button
                  v-if="hasError"
                  @click="retryProcess()"
                  class="btn btn-primary mb-3 mb-sm-0"
                  icon="fa fa-repeat"
                >
                  Tentar novamente
                </base-button>
              </div>
              <b-message
                v-if="hasError"
                type="is-danger"
              >
                <span v-html="$t(`errors.${errorMsg}`)" />
              </b-message>
            </div>
          </div>
          <slide-y-up-transition v-if="alert.show">
            <div class="col-lg-12 text-center alert-message">
              <div class="d-flex flex-column justify-space-between align-center">
                <div class="flex-item item flex-image">
                  <img
                    v-lazy="'img/icons/common/fail.png'"
                    class="card-img"
                  >
                </div>
              </div>
              <h1 class="display-3 ">
                {{ alert.title }}
              </h1>
              <p>{{ alert.message }}</p>
              <base-button
                @click="alert.action()"
                class="btn btn-primary mb-3 mb-sm-0"
              >
                {{ alert.textButton }}
              </base-button>
            </div>
          </slide-y-up-transition>
        </div>
      </div>
    </div>
    <!-- Marca do cliente na captura: mesma origem usada na FaceTec (client.logo).
         Fica sobre o diálogo do SDK, que não expõe slot de branding. -->
    <div
      v-if="showBranding"
      class="selfie-branding"
    >
      <img
        :src="client.logo"
        @error="brandingFailed = true"
        alt=""
      >
    </div>
  </div>
</template>
<script>
import { AppUI, Base64, FaceSDK, AsThreshold, Template, TransactionMode } from '@identy/identy-face'
import { mapActions, mapGetters } from 'vuex'
import {
  TRANSLATE_SELFIE
} from '@/config/translateSelfie'
import { SlideYUpTransition } from 'vue2-transitions'
import IncogniaWebSdk from '@incognia/web-sdk'
import LoadingPreparation from '@/components/LoadingPreparation.vue'
import { resolveBiometricTheme } from '@/config/biometricTheme'
import { SELFIE_MESSAGES } from '@/config/selfieMessages'

// Códigos do SDK Identy (Table 6 da documentação Web V7).
// Estes impedem a captura neste dispositivo/navegador e têm texto próprio do
// SDK para explicar o motivo, então viram tela de erro com ação de retentativa.
//   100 navegador/dispositivo não suportado   101 sem câmera / WebRTC ausente
//   103 versão do navegador não suportada     104 permissão de câmera negada
//   500 falha de conexão com o servidor       501 tempo de captura esgotado
const UNRECOVERABLE_CAPTURE_ERROR_CODES = [100, 101, 103, 104, 500, 501]

// 202 = FEEDBACK_DIALOG_CLOSED: o usuário fechou a captura. Não é falha.
const CAPTURE_CLOSED_BY_USER_CODE = 202

// 600 = FEEDBACK_LICENCE_INVALID: licença expirada ou inválida. Retentar não
// resolve e o texto do SDK não ajuda o usuário, então vira alerta terminal.
const CAPTURE_LICENCE_ERROR_CODE = 600
import { cameraPermissionMixin } from '@/mixins/camera-permission-mixin'

export default {
  name: 'SelfieView',
  components: {
    SlideYUpTransition,
    LoadingPreparation
  },
  mixins: [cameraPermissionMixin],
  data() {
    return {
      selfieActive: false,
      validationAttempts: 0,
      active: false,
      isInitializingCapture: false,
      isSubmittingSelfie: false,
      errorMsg: '',
      hasError: false,
      deviceToken: '',
      alert: {
        show: false,
        type: '',
        title: '',
        message: '',
        action: false,
        textButton: ''
      },
      brandingFailed: false,
      isPreparingEnvironment: true,
      startCapture: false
    }
  },
  computed: {
    ...mapGetters({
      client: 'client',
      riskDevice: 'riskDevice',
      riskDeviceId: 'riskDeviceId',
      device: 'device'
    }),
    maxRetryFaceCapture() {
      const limit = Number(this.client?.['retry-face-capture'])
      return Number.isFinite(limit) && limit > 0 ? limit : 5
    },
    isMobile() {
      return this.device === 'mobile'
    },
    messages() {
      return SELFIE_MESSAGES
    },
    // Só durante a captura em si: as telas de espera têm layout próprio.
    showBranding() {
      return Boolean(
        this.selfieActive &&
        !this.isInitializingCapture &&
        !this.isSubmittingSelfie &&
        !this.brandingFailed &&
        this.hasValidLogo
      )
    },
    // A API pode serializar ausência de logo como as strings "null"/"undefined";
    // nesses casos o <img> quebraria e renderizaria o texto do alt sobre a captura.
    hasValidLogo() {
      const logo = this.client.logo

      return typeof logo === 'string' &&
        logo.trim() !== '' &&
        !['null', 'undefined'].includes(logo.trim().toLowerCase())
    }
  },
  created() {
    this.initializePage()
  },
  methods: {
    ...mapActions([
      'validSelfieTransaction',
      'resetProcessUser',
      'validStepTransaction'
    ]),
    async initializePage() {
      this.resetAlert()
      const isValidStep = await this.validStep()

      if (!isValidStep) {
        this.isPreparingEnvironment = false
        return
      }

      await this.initRiskDevice()
      const hasCameraPermission = await this.validateCameraPermission()

      if (!hasCameraPermission) {
        this.isPreparingEnvironment = false
        return
      }

      try {
        await FaceSDK.preInitialize({
          URL: `${process.env.VUE_APP_BASE_API}/hive-liveness-portal/selfie/models`
        },
        {
          URL: {
            url: `${process.env.VUE_APP_BASE_API}/hive-liveness-portal/selfie/pub-key`,
            headers: [{
              name: 'LogAPITrigger',
              value: 'true'
            }]
          }
        }, {
          URL: `${process.env.VUE_APP_BASE_API}/hive-liveness-portal/selfie/timer`
        }
        )
      } catch (err) {
        console.error('FaceSDK preInitialize error:', err)
        this.alert = {
          show: true,
          type: 'danger',
          title: SELFIE_MESSAGES.terminalTitle,
          message: SELFIE_MESSAGES.environmentFailure,
          action: this.retryProcess,
          textButton: SELFIE_MESSAGES.buttonRetry,
          img: 'fail'
        }
      } finally {
        this.isPreparingEnvironment = false
      }
    },
    async validStep() {
      const { success, data } = await this.validStepTransaction({ step: 'selfie' })

      if (success) {
        const { data: dataValid } = data
        if (dataValid.success) {
          this.licenceKey = dataValid.key
          this.keyEncrypt = atob(dataValid.keyEncryption)
          this.textKey = atob(dataValid.textKey)
          this.validInit = true
          return true
        } else {
          this.alert = {
            show: true,
            type: 'danger',
            title: SELFIE_MESSAGES.terminalTitle,
            message: SELFIE_MESSAGES.invalidStep,
            action: this.restartProcess,
            textButton: SELFIE_MESSAGES.buttonRestart,
            img: 'fail'
          }
          return false
        }
      } else {
        this.alert = {
          show: true,
          type: 'danger',
          title: SELFIE_MESSAGES.terminalTitle,
          message: this.$t('errors.error_default_restart_transaction'),
          action: this.restartProcess,
          textButton: SELFIE_MESSAGES.buttonRestart,
          img: 'fail'
        }
        return false
      }
    },
    async initRiskDevice() {
      if (this.riskDevice && !this.deviceToken) {
        await IncogniaWebSdk.init({ appId: this.riskDeviceId })
        this.deviceToken = await IncogniaWebSdk.generateRequestToken()
      }
    },
    resetAlert() {
      this.alert = {
        show: false,
        type: '',
        title: '',
        message: '',
        action: false,
        textButton: ''
      }
    },
    resetCaptureState() {
      this.selfieActive = false
      this.active = false
      this.isInitializingCapture = false
      this.isSubmittingSelfie = false
      this.hasError = false
      this.errorMsg = ''
      this.startCapture = false
      this.resetAlert()
    },
    // Volta para a tela de preparação com a ação "Estou pronto" disponível.
    // Usado quando o usuário fecha a captura ou quando o SDK encerra sem resultado.
    resetToPreparation() {
      this.brandingFailed = false
      this.resetCaptureState()
    },
    openRetryAlert() {
      this.selfieActive = false
      this.alert = {
        show: true,
        type: 'danger',
        title: SELFIE_MESSAGES.retryTitle,
        message: SELFIE_MESSAGES.retry,
        action: this.capture,
        textButton: SELFIE_MESSAGES.buttonRetry
      }
    },
    openTerminalAlert(message, action = this.retryProcess, textButton = SELFIE_MESSAGES.buttonRetry) {
      this.selfieActive = false
      this.alert = {
        show: true,
        type: 'danger',
        title: SELFIE_MESSAGES.terminalTitle,
        message,
        action,
        textButton
      }
    },
    // openCameraBlockedAlert, validateCameraPermission e openCameraSettings
    // são fornecidos pelo cameraPermissionMixin
    shouldOfferRetryFromFallback(dataAnalisys = {}) {
      const hasExplicitTerminalResponse = Boolean(dataAnalisys?.nextStep) ||
        Boolean(dataAnalisys?.message && dataAnalisys.message !== 'selfie_invalid')
      return !hasExplicitTerminalResponse && this.validationAttempts < this.maxRetryFaceCapture
    },
    async retryProcess() {
      this.resetCaptureState()
      this.isPreparingEnvironment = true
      await this.initializePage()
    },
    async capture() {
      if (this.isPreparingEnvironment || this.isInitializingCapture || this.isSubmittingSelfie) {
        return
      }

      this.isInitializingCapture = true
      const hasCameraPermission = await this.validateCameraPermission({ requestIfNeeded: true })

      if (!hasCameraPermission) {
        this.isInitializingCapture = false
        return
      }

      this.startCapture = true
      this.selfieActive = true
      this.isSubmittingSelfie = false
      this.hasError = false
      this.errorMsg = ''
      this.resetAlert()
      this.runCaptureFace(TransactionMode.CAPTURE)
    },
    runCaptureFace(transaction) {
      if (!transaction) {
        transaction = TransactionMode.capture
      }

      // Paleta unica: o que a API do cliente enviar prevalece sobre o default da proposta de UX
      const theme = resolveBiometricTheme(this.client)

      const options = {
        enableAS: true,
        requiredTemplates: [Template.PNG],
        showCaptureTraining: false,
        base64EncodingFlag: Base64.NO_WRAP,
        allowClose: true,
        enableEyesStatusDetector: true,
        skipSupportCheck: false,
        allowFX: true,
        backend: 'wasm',
        transaction: {
          type: transaction
        },
        appUI: AppUI.STANDARD,
        allowCameraSelect: false,
        asThreshold: AsThreshold.HIGHEST,
        assisted: false,
        graphics: {
          canvas: {
            label: theme.feedbackText,
            canvasBackground: theme.surface,
            labelBackground: theme.feedbackBackground,
            ovalBorderBackground: theme.ovalNormal,
            ovalBorderErrorBackground: theme.ovalAdjust,
            tickerDefaultBackground: theme.progressTrack,
            tickerActiveBackground: theme.progressFill
          }
        },
        localization: {
          en: TRANSLATE_SELFIE
        }
      }

      options['enableGImage'] = true

      const faceSDK = new FaceSDK(options)

      faceSDK.initialize().then(() => {
        console.log('Valide selfie start...')
        this.isInitializingCapture = false
        faceSDK.capture().then((blob) => {
          // Sem imagem não há o que validar (captura encerrada antes do resultado).
          if (!blob) {
            this.resetToPreparation()
            return
          }

          this.active = false
          this.selfieActive = false
          return this.sendValidationSelfie(faceSDK, blob)
        }).catch((err) => {
          const code = err?.code

          if (code === CAPTURE_CLOSED_BY_USER_CODE) {
            // Escolha do usuário, não falha: volta para a preparação sem alarde.
            this.resetToPreparation()
            return
          }

          console.error('Face capture error:', err)

          if (code === CAPTURE_LICENCE_ERROR_CODE) {
            this.resetToPreparation()
            this.openTerminalAlert(SELFIE_MESSAGES.environmentFailure)
            return
          }

          // Erros de suporte/ambiente ficam na tela de erro com o texto do SDK.
          if (UNRECOVERABLE_CAPTURE_ERROR_CODES.includes(code)) {
            this.errorMsg = err.getLocalizedString()
            this.hasError = true
            this.isInitializingCapture = false
            this.isSubmittingSelfie = false
            this.selfieActive = false
            this.active = false
            return
          }

          // Qualquer outro encerramento devolve o usuário para a preparação,
          // com ação imediata em vez de uma tela sem saída.
          this.resetToPreparation()
        })
      }).catch((err) => {
        console.error('Face capture init error:', err)
        this.resetToPreparation()
      })
    },
    async sendValidationSelfie(sdk, capresult) {
      this.isSubmittingSelfie = true
      try {
        const { success, data, error } = await this.validSelfieTransaction({
          payload: capresult,
          deviceToken: this.deviceToken
        })
        if (success) {
          this.validationAttempts++
          const { data: dataAnalisys } = data

          if (dataAnalisys.retry) {
            this.openRetryAlert()
            return
          }

          if (dataAnalisys.success && dataAnalisys.nextStep) {
            // O portal é isento quanto ao resultado da biometria: a conclusão é
            // idêntica tenha a validação passado ou não. Qualquer toast aqui
            // denunciaria o veredito pela presença ou pela cor. A mensagem do
            // cliente já aparece na tela de conclusão (text-completed-title).
            this.$router.push({
              path: dataAnalisys.nextStep
            })
            return
          }

          if (this.shouldOfferRetryFromFallback(dataAnalisys)) {
            this.openRetryAlert()
            return
          }

          if (this.validationAttempts >= this.maxRetryFaceCapture) {
            this.openTerminalAlert(
              SELFIE_MESSAGES.attemptsExceeded,
              this.restartProcess,
              SELFIE_MESSAGES.buttonRestart
            )
            return
          }

          this.openTerminalAlert(SELFIE_MESSAGES.validationFailure)
        } else {
          const erroData = error.data
          const { message: messageKey } = erroData

          this.openTerminalAlert(this.$t(`errors.${messageKey}`) || SELFIE_MESSAGES.genericFailure)
        }
      } catch (error) {
        console.error('Erro ao validar selfie:', error)
        this.openTerminalAlert(SELFIE_MESSAGES.validationFailure)
      } finally {
        this.isSubmittingSelfie = false
      }
    },
    async restartProcess() {
      const link = await this.resetProcessUser()
      this.$router.push({
        path: '/',
        query: {
          y: link
        }
      })
    }
  }
}
</script>
<style lang="scss">
.scrollable-container {
  overflow-y: auto;
  max-height: 100vh;
}
.page-selfie {
  height: 100%;
  .flex-image {
    margin-top: 4rem;
  }
  &.is-selfie-active {
    background-color: #ffffff!important;
    height: 100%;
  }
  .text-center {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .card-img {
    max-width: 180px!important;
    max-height: 180px;
    margin: .8rem 0;
  }
  .alert-message {
    display: block!important;
    p {
      margin-bottom: .8rem;
    }
  }
  .position-relative,
  .container,
  .row {
    height: 100%;
  }
  .btn {
    max-height: 43px;
  }
  img,
  video {
    height: auto;
    max-width: none!important;
  }
}
// O diálogo do SDK Identy chega a z-index 100001; a marca precisa ficar acima.
.selfie-branding {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 100002;
  display: flex;
  justify-content: center;
  padding: 1rem;
  pointer-events: none;

  img {
    width: auto;
    max-width: 40vw!important;
    max-height: 2.25rem;
    height: auto;
  }
}

.selfie-ilustration {
  width: 10rem;
  height: 10rem;
  margin-bottom: 1rem;
}
.instructions {
  display: flex;
  flex-direction: column;
  margin: 1rem 2.5rem 1rem 2.5rem;
  p {
    text-align: left;
    font-size: 1rem;
    font-weight: 500;
    color: #111214;
    padding: 0.5rem 0;
    padding-left: 0.8rem;
  }
  .content-instruction {
    display: flex;
    align-items: center;
    width: 100%;
    border-bottom: 1px solid #E5E7EB;
    font-family: var(--font-family-text)!important;
  }
}
@media (min-width: 768px) {
  .scrollable-container {
    max-height: 100vh;
  }
  .instructions {
    margin: 0 1.5rem 1rem 1.5rem;
  }
}
</style>
